import React from 'react';

/** Shared detail-card section shell — title (+ optional count) on the left, an optional
 *  right-side control (add button or custom node) on the right, and either the section's
 *  content or a dashed-border empty-state message below. Used by both the Property details
 *  card (Photos/Files/Notes/Measurements/Tasks) and the Engagement/Workflow card (Available
 *  actions/Engagement Holder/Access restriction/Pending Approvals) so both surfaces share one
 *  section chrome instead of each hand-rolling its own header markup. */
export function CardSection({
  title, emptyText, onAdd, addLabel, count, rightSlot, children,
}: {
  /** Omit to render the section with no header row (e.g. when the content below is
   *  self-explanatory and a label would just be noise) — content/empty-state still render. */
  title?: string;
  emptyText?: string;
  onAdd?: () => void;
  addLabel?: string;
  count?: number;
  /** Custom right-aligned header control (e.g. a "current value" link) — takes precedence over
   *  the default add-button rendered from `onAdd`/`addLabel`. */
  rightSlot?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const hasContent = React.Children.count(children) > 0 && !emptyText;
  const hasHeader = !!title || !!rightSlot || !!onAdd;
  return (
    <div className="flex flex-col gap-2">
      {hasHeader && (
        <div className="flex items-center justify-between">
          {title && (
            <h3 className="text-xs font-semibold text-ui-text">
              {title}{count != null && count > 0 ? ` (${count})` : ''}
            </h3>
          )}
          {rightSlot ?? (onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent/10 text-accent-text hover:bg-accent/15 transition-colors flex items-center gap-1"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              {addLabel || 'Add'}
            </button>
          ))}
        </div>
      )}
      {hasContent ? (
        children
      ) : (
        emptyText && (
          <p className="text-xs text-ui-text3 px-3 py-3 rounded-lg border border-dashed border-ui-border bg-ui-subtle">
            {emptyText}
          </p>
        )
      )}
    </div>
  );
}

export default CardSection;
